import {
  convertEnumToYesNo,
  getFullName,
  obfuscateAndFormatSsn,
  stateNameByCode,
} from '@zorro/common-utils';
import { ContactIcon, IconPersonPurple } from '@zorro/shared/assets';
import { naicsCodesDatabase } from '@zorro/shared/databases';
import { formatDateEnLocale } from '@zorro/shared/formatters';
import { YesNo, businessEntityTypeLabelConfig } from '@zorro/types';
import { formatPhone } from '@zorro/web/ui-utils';

import {
  CompanyDetailsFormFields,
  hasBeneficialOwnersDescriptions,
} from '../../../CompanyDetailsForm';
import { EmployerLogo } from '../../../EmployerLogo';
import { PersonFormItem } from '../../../PersonFormArray';
import { AddEmployerStepType } from '../../AddEmployerStep/AddEmployerUtils';
import { ReviewCard, ReviewItemProps } from '../ReviewCard';

const mapBeneficialOwnersToReviewItems = (
  owners: PersonFormItem[],
  hasBeneficialOwners: YesNo = YesNo.YES,
  isFinalizationMode: boolean
): ReviewItemProps[] =>
  owners.flatMap(
    (
      { firstName, lastName, phone, email, address, dateOfBirth, ssn },
      index
    ) => [
      {
        name:
          hasBeneficialOwners === YesNo.YES
            ? `Beneficial owner ${index + 1} full name`
            : 'Officer full name',
        value: getFullName({ firstName, lastName }) || undefined,
        isRequired: isFinalizationMode,
        icon:
          hasBeneficialOwners === YesNo.YES ? (
            <ContactIcon />
          ) : (
            <IconPersonPurple />
          ),
      },
      {
        name: 'Phone number',
        value: phone ? formatPhone(phone) : undefined,
        isRequired: isFinalizationMode,
      },
      {
        name: 'Email address',
        value: email,
        isRequired: isFinalizationMode,
      },
      {
        name: 'Date of birth',
        value: dateOfBirth ? formatDateEnLocale(dateOfBirth) : undefined,
        isRequired: isFinalizationMode,
      },
      {
        name: 'Address',
        value: address,
        isRequired: isFinalizationMode,
        span: 8,
      },
      {
        name: 'SSN',
        value: obfuscateAndFormatSsn(ssn),
        isRequired: isFinalizationMode,
        span: 12,
      },
    ]
  );

type Props = {
  fields: CompanyDetailsFormFields;
  isSignedAgreementRequired: boolean;
  isFinalizationMode: boolean;
  employerId: string;
  isValid: boolean;
  isDon: boolean;
  shouldShowSignedAgreement?: boolean;
  isLynxPaymentsEnabled?: boolean;
};

export const CompanyDetailsCard = ({
  isSignedAgreementRequired,
  isFinalizationMode,
  employerId,
  fields,
  isValid,
  isDon,
  shouldShowSignedAgreement = true,
  isLynxPaymentsEnabled = false,
}: Props) => {
  'use no memo';
  const {
    name,
    address,
    legalName,
    mailingAddress,
    signedAgreement,
    agreementSignedDate,
    businessEntityType,
    taxId,
    taxIdType,
    logo,
    naicsCode,
    websiteUrl,
    stateOfIncorporation,
    yearsInBusiness,
    hasBeneficialOwners,
    beneficialOwners,
  } = fields;

  const serviceAgreementItems: ReviewItemProps[] = shouldShowSignedAgreement
    ? [
        { name: 'service-agreement-separator', isSeparator: true },
        { name: 'service-agreement', title: 'Zorro service agreement:' },
        {
          name: 'Signed agreement',
          value:
            signedAgreement &&
            !(signedAgreement instanceof File) &&
            signedAgreement.fileName,
          isRequired: isSignedAgreementRequired,
        },
        {
          name: 'Date agreement signed',
          value:
            agreementSignedDate !== undefined &&
            formatDateEnLocale(agreementSignedDate),
          isRequired: isSignedAgreementRequired,
        },
      ]
    : [];

  const beneficialOwnerItems: ReviewItemProps[] = isLynxPaymentsEnabled
    ? [
        { name: 'beneficial-owners-separator', isSeparator: true },
        {
          name: 'Does any individual own 25% or more?',
          value: hasBeneficialOwners
            ? `${convertEnumToYesNo(hasBeneficialOwners)}, ${hasBeneficialOwnersDescriptions[hasBeneficialOwners]}`
            : undefined,
          isRequired: isFinalizationMode,
          span: 12,
        },
        ...mapBeneficialOwnersToReviewItems(
          beneficialOwners ?? [],
          hasBeneficialOwners,
          isFinalizationMode
        ),
      ]
    : [];

  return (
    <ReviewCard
      isDon={isDon}
      isValid={isValid}
      title="Company details"
      employerId={employerId}
      step={AddEmployerStepType.COMPANY_DETAILS}
      items={[
        {
          name: 'general-information',
          title: 'General information:',
        },
        {
          name: 'Company name (DBA)',
          value: name,
          isRequired: true,
        },
        {
          name: 'HQ address',
          value: address,
          isRequired: isFinalizationMode,
          span: 8,
        },
        {
          name: 'Legal name',
          value: legalName,
          isRequired: isFinalizationMode,
        },
        {
          name: 'Mailing address',
          value: mailingAddress,
          isRequired: isFinalizationMode,
          span: 8,
        },
        ...(isLynxPaymentsEnabled
          ? [
              {
                name: 'Website URL',
                value: websiteUrl,
              },
            ]
          : []),
        {
          name: 'legal-tax-separator',
          isSeparator: true,
        },
        {
          name: 'legal-tax-information',
          title: 'Legal & tax information:',
        },
        {
          name: 'Business entity type',
          value:
            businessEntityType &&
            businessEntityTypeLabelConfig[businessEntityType],
          isRequired: isFinalizationMode,
        },
        {
          name: 'Tax ID',
          value: taxIdType && taxId && `${taxIdType} ${taxId}`,
          isRequired: isFinalizationMode,
        },
        ...(isLynxPaymentsEnabled
          ? [
              {
                name: 'State of incorporation',
                value:
                  stateOfIncorporation &&
                  (stateNameByCode[stateOfIncorporation] ??
                    stateOfIncorporation),
                isRequired: isFinalizationMode,
              },
              {
                name: 'Years in business',
                value: yearsInBusiness?.toString(),
                isRequired: isFinalizationMode,
              },
            ]
          : []),
        {
          name: 'NAICS code',
          value:
            naicsCode &&
            (() => {
              const naicsEntry = naicsCodesDatabase?.find(
                (entry) => entry.code === naicsCode
              );
              return naicsEntry
                ? `${naicsCode} - ${naicsEntry.title}`
                : naicsCode;
            })(),
          isRequired: isFinalizationMode,
        },
        {
          name: 'Company logo',
          value: logo && typeof logo === 'string' && (
            <EmployerLogo logoUrl={logo} />
          ),
          isRequired: isFinalizationMode,
        },
        ...beneficialOwnerItems,
        ...serviceAgreementItems,
      ]}
    />
  );
};
